import React, { useMemo, useRef, useEffect } from "react";
import { Autocomplete, TextField, InputAdornment, ListItemButton, ListItemText, Chip, Box } from "@mui/material";
import { SearchRounded } from "@mui/icons-material";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAppContext } from "context";
import { buildMenu } from "components/body/menuConfig";
import { getSearchMeta } from "components/body/searchMeta";

const escapeRe = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

// Wrap the portions of `text` that match any whitespace-separated token of
// `query` in a highlight <mark>. Case-insensitive, multi-term.
const highlightMatch = (text, query) => {
  const str = String(text || "");
  const q = (query || "").trim();
  if (!q || !str) return str;
  const tokens = q.split(/\s+/).filter(Boolean).map(escapeRe);
  if (!tokens.length) return str;
  const re = new RegExp(`(${tokens.join("|")})`, "ig");
  // String.split with a capturing group → matches land on odd indices.
  return str.split(re).map((part, i) =>
    i % 2 === 1 ? (
      <mark
        key={i}
        style={{
          backgroundColor: "var(--primaryMD-color)",
          color: "var(--primaryDark-color)",
          fontWeight: 700,
          borderRadius: 3,
          padding: "0 1px",
        }}
      >
        {part}
      </mark>
    ) : (
      <React.Fragment key={i}>{part}</React.Fragment>
    )
  );
};

/**
 * Navigation omnibox for the modern shell. Flattens the access-gated menu tree
 * into searchable leaves enriched with a subtitle + keyword synonyms
 * (components/body/searchMeta), so users find features by what they mean — e.g.
 * "chart of accounts" → COA Management. No backend; instant. Ctrl/⌘-K or "/"
 * focuses it.
 */
const GlobalSearch = () => {
  const { access, settings } = useAppContext();
  const { t } = useTranslation(["nav", "common"]);
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const isMac = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent || "");

  const options = useMemo(() => {
    const groups = buildMenu(access, settings, t);
    const flat = [];
    groups.forEach((g) => {
      if (g.children && g.children.length) {
        g.children.forEach((c) => flat.push({ title: c.title, path: c.path, group: g.title }));
      } else if (g.path) {
        flat.push({ title: g.title, path: g.path, group: g.title });
      }
    });
    // De-dupe by path, then enrich with subtitle + keyword synonyms.
    const seen = new Set();
    return flat
      .filter((o) => (seen.has(o.path) ? false : seen.add(o.path)))
      .map((o) => {
        const meta = getSearchMeta(o.path);
        const subtitle = meta.subtitle || "";
        const keywords = meta.keywords || [];
        const hay = [o.title, o.group, subtitle, ...keywords].join(" ").toLowerCase();
        return { ...o, subtitle, keywords, _hay: hay };
      });
  }, [access, settings, t]);

  // Multi-term match across title + group + subtitle + keywords; every typed
  // token must appear somewhere. Results ranked by how well the TITLE matches.
  const filterOptions = (opts, { inputValue }) => {
    const q = (inputValue || "").trim().toLowerCase();
    if (!q) return opts;
    const tokens = q.split(/\s+/).filter(Boolean);
    const matched = opts.filter((o) => tokens.every((tk) => o._hay.includes(tk)));
    const rank = (o) => {
      const title = o.title.toLowerCase();
      if (title.startsWith(q)) return 0;
      if (title.includes(q)) return 1;
      if (tokens.every((tk) => title.includes(tk))) return 2;
      return 3; // matched only via subtitle/keywords
    };
    return matched.sort((a, b) => rank(a) - rank(b) || a.title.localeCompare(b.title));
  };

  // Global keyboard shortcut: Ctrl/Cmd-K or "/" focuses the search.
  useEffect(() => {
    const onKey = (e) => {
      const tag = (e.target?.tagName || "").toLowerCase();
      const typing = tag === "input" || tag === "textarea" || e.target?.isContentEditable;
      if ((e.key === "k" && (e.ctrlKey || e.metaKey)) || (e.key === "/" && !typing)) {
        e.preventDefault();
        inputRef.current?.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <Autocomplete
      options={options}
      filterOptions={filterOptions}
      getOptionLabel={(o) => o?.title || ""}
      isOptionEqualToValue={(o, v) => o.path === v.path}
      size="small"
      fullWidth
      autoHighlight
      blurOnSelect
      clearOnBlur
      value={null}
      onChange={(e, val) => val?.path && navigate(val.path)}
      noOptionsText={t("common:noData", "No matches")}
      forcePopupIcon={false}
      ListboxProps={{ style: { maxHeight: 440, padding: 6 } }}
      componentsProps={{
        paper: {
          sx: {
            mt: 1,
            borderRadius: "14px",
            border: "1px solid #eef2f7",
            boxShadow: "var(--shadow-lg)",
            overflow: "hidden",
            "& .MuiAutocomplete-option": { borderRadius: "10px", margin: "2px 0" },
          },
        },
      }}
      renderOption={(props, option, { inputValue }) => {
        // Render each result as a real router <Link> (anchor with href) so the
        // browser's native "Open in New Tab" (right-click / middle-click /
        // Ctrl+click) works. A plain left-click still selects → SPA-navigates.
        const { onClick, ...rest } = props;
        return (
        <ListItemButton
          {...rest}
          component={Link}
          to={option.path}
          onClick={(e) => {
            // Modifier / middle click → let the browser open a new tab; don't
            // also select the option (which would navigate the current tab).
            if (e.ctrlKey || e.metaKey || e.shiftKey || e.button === 1) {
              e.stopPropagation();
              return;
            }
            e.preventDefault(); // selection (below) handles in-app navigation
            onClick?.(e);
          }}
          key={option.path}
          sx={{ py: 0.5, alignItems: "flex-start", color: "inherit", textDecoration: "none" }}
        >
          <ListItemText
            primary={
              <span className="flex items-center gap-2">
                <span className="text-[14px] font-medium text-slate-700">
                  {highlightMatch(option.title, inputValue)}
                </span>
                {option.group && option.group !== option.title && (
                  <Chip
                    label={option.group}
                    size="small"
                    sx={{ height: 18, fontSize: 10, bgcolor: "var(--primarySM-color)", color: "var(--primary-color)" }}
                  />
                )}
              </span>
            }
            secondary={option.subtitle ? highlightMatch(option.subtitle, inputValue) : undefined}
            secondaryTypographyProps={{ fontSize: 12, color: "#94a3b8" }}
          />
        </ListItemButton>
        );
      }}
      renderInput={(params) => (
        <Box sx={{ position: "relative", width: "100%" }}>
          {/* Search icon — absolutely centered, independent of MUI's adornment
              internals (which baseline-align the SVG and would not center). */}
          <SearchRounded
            sx={{
              position: "absolute",
              insetInlineStart: "16px",
              top: "50%",
              transform: "translateY(-50%)",
              fontSize: 20,
              color: "var(--primary-color)",
              pointerEvents: "none",
              zIndex: 1,
            }}
          />
          <TextField
            {...params}
            inputRef={inputRef}
            placeholder={t("common:searchPlaceholder", "Search pages, reports, settings…")}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <InputAdornment position="end" sx={{ display: { xs: "none", lg: "flex" } }}>
                  <kbd
                    style={{
                      fontSize: 11,
                      fontWeight: 600,
                      lineHeight: 1,
                      color: "#64748b",
                      background: "#fff",
                      border: "1px solid #e2e8f0",
                      borderRadius: 6,
                      padding: "3px 6px",
                      boxShadow: "0 1px 0 #e2e8f0",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {isMac ? "⌘K" : "Ctrl K"}
                  </kbd>
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                minHeight: 42,
                backgroundColor: "#fff",
                borderRadius: "999px",
                // !important beats MUI Autocomplete's own inputRoot padding
                // (set via a higher-specificity attribute selector).
                paddingInlineStart: "44px !important",
                paddingInlineEnd: "14px",
                boxShadow: "var(--shadow-sm)",
                transition: "box-shadow .2s, border-color .2s, background-color .2s",
                "& fieldset": { borderColor: "#e2e8f0" },
                "&:hover": { backgroundColor: "#fff", boxShadow: "var(--shadow-md)" },
                "&:hover fieldset": { borderColor: "#cbd5e1" },
                "&.Mui-focused": {
                  backgroundColor: "#fff",
                  boxShadow: "var(--shadow-md), 0 0 0 4px var(--primarySM-color)",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "var(--primary-color)",
                  borderWidth: "1px",
                },
              },
              "& .MuiOutlinedInput-input, & .MuiAutocomplete-input": { fontSize: 14 },
              "& input::placeholder": { color: "#94a3b8", opacity: 1 },
            }}
          />
        </Box>
      )}
    />
  );
};

export default GlobalSearch;
